import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ActivityIndicator, Image, StyleSheet, Text, View } from 'react-native';
import Svg, { Defs, LinearGradient, Rect, Stop } from 'react-native-svg';
import { useDispatch, useSelector } from 'react-redux';

import {
	BGRorB_Thumb,
	BGScratch_Thumb,
	BGSlot_Thumb,
	JackpotChipPng,
	PlayerIconPng,
} from '../../../assets';
import { IconCloseSvg } from '../../../assets/inventory';
import { analyticsEvents } from '../../../services/firebase/analytics-events.service';
import {
	JackpotHistoryItem,
	jackpotHistoryRequest,
} from '../../../store/jackpot/miniGamesRewardsActions';
import {
	selectJackpotHistory,
	selectJackpotHistoryError,
	selectJackpotHistoryLoading,
} from '../../../store/jackpot/miniGamesRewardsReducer';
import { formatDate } from '../../../utils/helpers/date';
import { useSvgId } from '../../../utils/hooks/useSvgId';
import { RedesignScrollView } from '../common/RedesignScrollView';
import { Pressable } from '../common/Touchable';
import {
	groupThousands,
	PANEL_FILL,
	PANEL_RIM,
	ROW_FILL,
	TOURNAMENT_FONT as FONT,
	tournamentColors as colors,
} from '../Tournaments/tournamentModalTheme';
import RedesignModal from './RedesignModal';
import { useRedesignScale } from './useRedesignScale';

/**
 * The mini-game jackpot winners, opened from the jackpot bar. There is no Figma frame for it, so
 * it is put together from the redesign's own pieces: the tournament sheet's panel and title bar
 * ("Modal / Tournament Rules", 5157:14681) around the leaderboard table and rows of "Leaderboard"
 * (5307:46875), the same table RankingScreen draws. Numbers are in the 390-wide mobile design
 * space and scaled once.
 */
const PANEL = { height: 600, radius: 10, paddingX: 12, paddingY: 20, gap: 16 };

/** Table hairline, "border/red" on the leaderboard frame. */
const TABLE_RIM = '#6B1A10';

/** The winning tier still reads off the amount, as it did in the old table. */
const TIER_COLORS: Record<string, string> = {
	gold: colors.accent,
	silver: '#E4E1DF',
	bronze: '#F0A872',
};

const formatTime = (dateString: string) =>
	new Date(dateString).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });

const gameOf = (gameName: string, t: (key: string, options?: any) => string) => {
	const name = gameName?.toLowerCase() ?? '';
	if (name.includes('black') || name.includes('red'))
		return {
			label: t('T_SITE_MODAL_JACKPOT_GAME_BLACK_AND_RED', { defaultValue: 'Black & Red' }),
			art: BGRorB_Thumb,
		};
	if (name.includes('slot'))
		return {
			label: t('T_SITE_MODAL_JACKPOT_GAME_SLOT', { defaultValue: 'Slot' }),
			art: BGSlot_Thumb,
		};
	if (name.includes('scratch'))
		return {
			label: t('T_SITE_MODAL_JACKPOT_GAME_SCRATCH_CARD', { defaultValue: 'Scratch Card' }),
			art: BGScratch_Thumb,
		};
	return { label: gameName, art: PlayerIconPng };
};

type Styles = ReturnType<typeof createStyles>;

const VerticalFill = ({ stops }: { stops: Array<[string, string]> }) => {
	const id = useSvgId('jackpotHistoryFill');
	return (
		<View pointerEvents='none' style={StyleSheet.absoluteFill}>
			<Svg width='100%' height='100%'>
				<Defs>
					<LinearGradient id={id} x1='0' y1='0' x2='0' y2='1'>
						{stops.map(([offset, color]) => (
							<Stop key={offset} offset={offset} stopColor={color} />
						))}
					</LinearGradient>
				</Defs>
				<Rect x='0' y='0' width='100%' height='100%' fill={`url(#${id})`} />
			</Svg>
		</View>
	);
};

const JackpotHistoryRow = ({
	item,
	styles,
	t,
}: {
	item: JackpotHistoryItem;
	styles: Styles;
	t: (key: string, options?: any) => string;
}) => {
	const game = gameOf(item.gameName, t);
	return (
		<View style={styles.row}>
			<VerticalFill stops={ROW_FILL} />
			<View style={styles.gameRing}>
				<Image source={game.art} resizeMode='cover' style={styles.gameArt} />
			</View>
			<View style={styles.playerCell}>
				<Text numberOfLines={1} style={styles.playerName}>
					{item.playerName ?? item.playerId}
				</Text>
				<Text numberOfLines={1} style={styles.rowDetail}>
					{game.label}
				</Text>
			</View>
			<View style={styles.valueCell}>
				<View style={styles.amountRow}>
					<Image source={JackpotChipPng} resizeMode='contain' style={styles.chip} />
					<Text
						numberOfLines={1}
						adjustsFontSizeToFit
						minimumFontScale={0.7}
						style={[
							styles.amount,
							{ color: TIER_COLORS[item.jackpotType?.toLowerCase()] ?? colors.accent },
						]}
					>
						{groupThousands(item.amount)}
					</Text>
				</View>
				<Text numberOfLines={1} style={styles.rowDetail}>
					{`${formatDate(item.createdAt)} ${formatTime(item.createdAt)}`}
				</Text>
			</View>
		</View>
	);
};

type Props = {
	onClose: () => void;
};

const JackpotHistoryModal = ({ onClose }: Props) => {
	const dispatch = useDispatch();
	const { t } = useTranslation();
	const history = useSelector(selectJackpotHistory);
	const loading = useSelector(selectJackpotHistoryLoading);
	const error = useSelector(selectJackpotHistoryError);
	const scale = useRedesignScale({ fitHeight: PANEL.height });
	const styles = useMemo(() => createStyles(scale), [scale]);

	useEffect(() => {
		analyticsEvents.trackOpenJackpotHistory();
		dispatch(jackpotHistoryRequest());
	}, [dispatch]);

	const status = loading ? (
		<ActivityIndicator color={colors.gold300} />
	) : error ? (
		<Text style={[styles.status, styles.statusError]}>{error}</Text>
	) : !history?.length ? (
		<Text style={styles.status}>
			{t('T_SITE_MODAL_JACKPOT_NO_HISTORY', { defaultValue: 'No jackpots won yet' })}
		</Text>
	) : null;

	return (
		<RedesignModal
			scale={scale}
			onClose={onClose}
			panel={{
				height: PANEL.height,
				radius: PANEL.radius,
				fill: PANEL_FILL,
				rim: PANEL_RIM,
				overScreen: true,
			}}
		>
			<View style={styles.body}>
				<View style={styles.titleBar}>
					<Text style={styles.title}>
						{t('T_SITE_MODAL_JACKPOT_TITLE', { defaultValue: 'Jackpot History' }).toUpperCase()}
					</Text>
					<Pressable
						accessibilityRole='button'
						accessibilityLabel={t('T_SITE_A11Y_CLOSE_JACKPOT_HISTORY', {
							defaultValue: 'Close jackpot history',
						})}
						onPress={onClose}
						hitSlop={8}
						style={styles.close}
					>
						<IconCloseSvg width={24 * scale} height={24 * scale} />
					</Pressable>
				</View>

				<View style={styles.table}>
					<VerticalFill stops={ROW_FILL} />
					<View style={styles.tableHeader}>
						<Text style={[styles.headerText, styles.headerPlayer]}>
							{t('T_SITE_MODAL_JACKPOT_TABLE_NAME', { defaultValue: 'Player' })}
						</Text>
						<Text style={styles.headerText}>
							{t('T_SITE_MODAL_JACKPOT_TABLE_CHIPS', { defaultValue: 'Chips' })}
						</Text>
					</View>
					<View style={styles.divider} />
					{status ? (
						<View style={styles.statusBox}>{status}</View>
					) : (
						<RedesignScrollView
							contentContainerStyle={styles.rows}
							indicatorInsets={{ right: 0, top: 0, bottom: 0 }}
						>
							{history.map(item => (
								<JackpotHistoryRow key={item.id} item={item} styles={styles} t={t} />
							))}
						</RedesignScrollView>
					)}
				</View>
			</View>
		</RedesignModal>
	);
};

const createStyles = (scale: number) => {
	const s = (n: number) => n * scale;
	return StyleSheet.create({
		body: {
			flex: 1,
			paddingHorizontal: s(PANEL.paddingX),
			paddingVertical: s(PANEL.paddingY),
			gap: s(PANEL.gap),
		},
		titleBar: { flexDirection: 'row', alignItems: 'center', gap: s(8) },
		title: {
			flex: 1,
			fontFamily: FONT,
			fontWeight: '700',
			fontSize: s(24),
			lineHeight: s(24 * 1.3),
			color: colors.gold200,
		},
		close: { width: s(32), height: s(32), alignItems: 'center', justifyContent: 'center' },
		table: {
			flex: 1,
			minHeight: 0,
			paddingHorizontal: s(6),
			paddingVertical: s(8),
			gap: s(4),
			borderRadius: s(12),
			borderWidth: Math.max(1, scale),
			borderColor: TABLE_RIM,
			overflow: 'hidden',
		},
		tableHeader: {
			height: s(20),
			flexDirection: 'row',
			alignItems: 'center',
			gap: s(8),
			paddingLeft: s(8),
			paddingRight: s(12),
		},
		headerText: {
			fontFamily: FONT,
			fontWeight: '600',
			fontSize: s(12),
			lineHeight: s(14),
			color: colors.muted,
		},
		headerPlayer: { flex: 1 },
		divider: {
			height: 1,
			marginHorizontal: s(8),
			marginVertical: s(4),
			backgroundColor: colors.borderSubtle,
		},
		// 10 on the right keeps the rows clear of the scroll bar.
		rows: { gap: s(6), paddingTop: s(2), paddingRight: s(10) },
		row: {
			height: s(55),
			flexDirection: 'row',
			alignItems: 'center',
			gap: s(12),
			paddingHorizontal: s(10),
			borderRadius: s(8),
			borderWidth: Math.max(1, scale),
			borderColor: colors.rowBorder,
			overflow: 'hidden',
		},
		gameRing: {
			width: s(40),
			height: s(40),
			borderRadius: s(20),
			borderWidth: s(2),
			borderColor: colors.buttonRim,
			backgroundColor: '#000000',
			alignItems: 'center',
			justifyContent: 'center',
			overflow: 'hidden',
		},
		gameArt: { width: s(36), height: s(36), borderRadius: s(18) },
		playerCell: { flex: 1, minWidth: 0 },
		playerName: {
			fontFamily: FONT,
			fontWeight: '700',
			fontSize: s(14),
			lineHeight: s(16),
			color: colors.gold50,
		},
		rowDetail: {
			fontFamily: FONT,
			fontWeight: '500',
			fontSize: s(12),
			lineHeight: s(14),
			color: colors.muted,
		},
		valueCell: { alignItems: 'flex-end' },
		amountRow: { flexDirection: 'row', alignItems: 'center', gap: s(6) },
		chip: { width: s(20), height: s(20) },
		amount: {
			minWidth: s(74),
			maxWidth: s(96),
			fontFamily: FONT,
			fontWeight: '700',
			fontSize: s(16),
			lineHeight: s(20),
			textAlign: 'right',
		},
		statusBox: { flex: 1, alignItems: 'center', justifyContent: 'center' },
		status: {
			fontFamily: FONT,
			fontWeight: '600',
			fontSize: s(14),
			lineHeight: s(18),
			color: colors.muted,
			textAlign: 'center',
		},
		statusError: { color: '#FF4D3D' },
	});
};

export default JackpotHistoryModal;
